import { config } from "@/shared/lib/config";
import { fetcher } from "@/shared/lib/fetcher";

/** ຈາກ `audit_record` + `audit_plan` (schema internal-audit) — ຂອບເຂດຕາມຕຳແໜ່ງຜູ້ກວດໃນແຜນ */
export type InternalAuditRecordRowDTO = {
  id: string;
  auditDate: string;
  overallResult: "pass" | "fail" | "needs_improvement";
  recordStatus: "draft" | "completed";
  planName: string;
  auditTypeName: string;
  departmentName: string;
  qcStandardName: string | null;
  auditorId: string;
  auditorName: string | null;
};

export type InternalAuditRecordsListDTO = {
  data: InternalAuditRecordRowDTO[];
  meta: { total: number; limit: number; offset: number };
};

/** ລາຍການແຜນ — ກອງດ້ວຍ auditor_position_id = ຕຳແໜ່ງຂອງຜູ້ລັອກອິນ */
export type AuditPlanRowDTO = {
  id: string;
  name: string;
  description: string | null;
  auditTypeName: string;
  departmentName: string;
  auditorPositionId: string;
  auditorPositionTitle: string;
  qcStandardName: string | null;
  nextAuditDate: string;
  status: "pending" | "in_progress" | "completed" | "cancelled";
  frequency: "monthly" | "quarterly" | "semi_annual" | "annual" | "ad_hoc";
};

export type AuditPlansListDTO = {
  data: AuditPlanRowDTO[];
  meta: { total: number; limit: number; offset: number };
};

/** ລາຍລະອຽດແຜນ (ສຳລັບຟອມບັນທຶກ) — ກອງດ້ວຍຕຳແໜ່ງຜູ້ກວດ */
export type AuditPlanDetailDTO = {
  id: string;
  name: string;
  description: string | null;
  auditTypeName: string;
  departmentName: string;
  auditorPositionId: string;
  auditorPositionTitle: string;
  qcStandardName: string | null;
  nextAuditDate: string;
  status: "pending" | "in_progress" | "completed" | "cancelled";
  frequency: "monthly" | "quarterly" | "semi_annual" | "annual" | "ad_hoc";
};

export type CreateAuditRecordResponseDTO = { recordId: string };

export type InternalAuditRecordDetailDTO = {
  record: {
    id: string;
    auditDate: string;
    auditScope: string;
    summary: string | null;
    overallResult: "pass" | "fail" | "needs_improvement";
    status: "draft" | "completed";
    auditorId: string;
    auditorName: string | null;
    planName: string;
    auditTypeName: string;
    departmentName: string;
    auditorPositionTitle: string | null;
    qcStandardName: string | null;
  };
  items: {
    id: string;
    title: string;
    description: string | null;
    orderIndex: number;
    result: "pass" | "fail" | "needs_improvement";
    notes: string | null;
  }[];
  attachments: { id: string; fileKey: string }[];
};

const baseUrl = `${config.apiUrl}/internal-audit`;

export const internalAuditApi = {
  async listPlans(params?: {
    limit?: number;
    offset?: number;
  }): Promise<AuditPlansListDTO> {
    const q = new URLSearchParams();
    if (params?.limit != null) q.set("limit", String(params.limit));
    if (params?.offset != null) q.set("offset", String(params.offset));
    const qs = q.toString();
    return fetcher.get<AuditPlansListDTO>(
      `${baseUrl}/plans${qs ? `?${qs}` : ""}`,
    );
  },

  async listRecords(params?: {
    limit?: number;
    offset?: number;
  }): Promise<InternalAuditRecordsListDTO> {
    const q = new URLSearchParams();
    if (params?.limit != null) q.set("limit", String(params.limit));
    if (params?.offset != null) q.set("offset", String(params.offset));
    const qs = q.toString();
    return fetcher.get<InternalAuditRecordsListDTO>(
      `${baseUrl}/records${qs ? `?${qs}` : ""}`,
    );
  },

  async getRecord(recordId: string): Promise<InternalAuditRecordDetailDTO> {
    return fetcher.get<InternalAuditRecordDetailDTO>(
      `${baseUrl}/records/${recordId}`,
    );
  },

  async getPlan(planId: string): Promise<AuditPlanDetailDTO> {
    return fetcher.get<AuditPlanDetailDTO>(`${baseUrl}/plans/${planId}`);
  },

  async createRecord(body: {
    auditPlanId: string;
    auditDate: string;
    auditScope: string;
    summary?: string | null;
    overallResult: "pass" | "fail" | "needs_improvement";
    status?: "draft" | "completed";
    items: {
      title: string;
      description?: string | null;
      result: "pass" | "fail" | "needs_improvement";
      notes?: string | null;
    }[];
    attachmentKeys?: string[];
  }): Promise<CreateAuditRecordResponseDTO> {
    return fetcher.post<CreateAuditRecordResponseDTO>(
      `${baseUrl}/records`,
      body,
    );
  },
};
